import { useMemo, useCallback } from 'react';
import { CategoryData, WeightCluster } from '@/lib/api';

interface UseScoreCalculationsProps {
    scores: Map<string, { score: number | null }>;
    allScores: Map<string, { score: number | null; reliability: number }>;
    categoryData: CategoryData[];
    weightsData: WeightCluster | null;
    allWeightsData: WeightCluster[];
    allCategoryData: Record<string, CategoryData[]>;
    enNameMap: Map<string, { enCategory: string; enSubcategory: string }>;
    getScoreKey: (cat: string, sub: string) => string;
    activeWeightProfile?: any;
}

export function useScoreCalculations({
    scores,
    allScores,
    categoryData,
    weightsData,
    allWeightsData,
    allCategoryData,
    enNameMap,
    getScoreKey,
    activeWeightProfile,
}: UseScoreCalculationsProps) {

    // Get score color based on value (0=red, 5=green)
    const getScoreColor = useCallback((score: number | null): string => {
        if (score === null) return 'bg-slate-100';
        const normalized = score / 5;
        if (normalized < 0.2) return 'bg-red-400';
        if (normalized < 0.4) return 'bg-orange-400';
        if (normalized < 0.6) return 'bg-yellow-400';
        if (normalized < 0.8) return 'bg-lime-400';
        return 'bg-green-400';
    }, []);

    // Calculate progress
    const progress = useMemo(() => {
        let total = 0;
        let scored = 0;

        // Helper to check if item has weight
        const hasWeight = (localCategory: string, localSubcategory: string): boolean => {
            // Default to true if no active profile (or static weights implies enabled)
            // But actually we should check static weights too if possible, though static usually has weights.
            if (!activeWeightProfile || !activeWeightProfile.weight_tree) return true;

            // Resolve EN names
            const mapEntry = enNameMap.get(`${localCategory}::${localSubcategory}`);
            const enCategory = mapEntry ? mapEntry.enCategory : localCategory;
            const enSubcategory = mapEntry ? mapEntry.enSubcategory : localSubcategory;

            // Assume cluster is valid since we are in a cluster context (weightsData.cluster)
            // But we need to find it in the profile
            const clusterNode = activeWeightProfile.weight_tree.find((n: any) =>
                n.key === weightsData?.cluster || n.name_en?.toUpperCase() === weightsData?.cluster
            );

            if (!clusterNode || clusterNode.weighting === 0) return false;

            const categoryNode = clusterNode.children?.find((c: any) => c.name_en === enCategory);
            if (!categoryNode || categoryNode.weighting === 0) return false;

            const subcategoryNode = categoryNode.children?.find((s: any) => s.name_en === enSubcategory);
            if (!subcategoryNode || subcategoryNode.weighting === 0) return false;

            return true;
        };

        for (const cat of categoryData) {
            for (const sub of cat.subcategories) {
                if (hasWeight(cat.category, sub.name)) {
                    total++;
                    const key = getScoreKey(cat.category, sub.name);
                    // Also check if scored
                    if (scores.get(key)?.score !== null && scores.get(key)?.score !== undefined) {
                        scored++;
                    }
                }
            }
        }
        return { total, scored, percentage: total > 0 ? Math.round((scored / total) * 100) : 0 };
    }, [categoryData, scores, getScoreKey, activeWeightProfile, weightsData, enNameMap]);

    // Calculate weighted score for a category (using subcategory weights)
    // Takes locale category name, maps to EN for weight lookups
    const calculateCategoryScore = useCallback((localeCategoryName: string): number | null => {
        if (!weightsData) return null;

        // Find the EN category name for this locale category
        let enCategoryName = localeCategoryName; // default fallback for EN locale
        for (const [localeKey, enNames] of enNameMap.entries()) {
            if (localeKey.startsWith(localeCategoryName + '::')) {
                enCategoryName = enNames.enCategory;
                break;
            }
        }

        // Try to get dynamic weights first
        let subcategories: { name: string; weighting: number }[] = [];
        let categoryWeight = 0;
        let useDynamic = false;

        if (activeWeightProfile && activeWeightProfile.weight_tree) {
            const clusterNode = activeWeightProfile.weight_tree.find((n: any) =>
                n.key === weightsData.cluster || n.name_en?.toUpperCase() === weightsData.cluster
            );

            if (clusterNode) {
                // Check if cluster is disabled
                if (clusterNode.weighting === 0) return null;

                const categoryNode = clusterNode.children?.find((c: any) => c.name_en === enCategoryName);
                if (categoryNode) {
                    categoryWeight = categoryNode.weighting;
                    // Check if category is disabled
                    if (categoryWeight === 0) return null;

                    subcategories = categoryNode.children?.map((s: any) => ({
                        name: s.name_en, // Use EN name
                        weighting: s.weighting
                    })) || [];
                    useDynamic = true;
                }
            }
        }

        // Fallback to static weights
        if (!useDynamic) {
            const weightCategory = weightsData.categories.find(c => c.name === enCategoryName);
            if (!weightCategory) return null;

            // Check static cluster weight if present (though weightsData doesn't explicitly have weighting usually, the file structure might)
            if (weightsData.weighting === 0) return null;

            categoryWeight = weightCategory.weighting;
            if (categoryWeight === 0) return null;

            subcategories = weightCategory.subcategories;
        }

        let totalWeight = 0;
        let weightedSum = 0;
        let hasAnyScore = false;

        for (const weightSub of subcategories) {
            // weightSub.name is EN, scores map uses EN keys
            const key = `${enCategoryName}::${weightSub.name}`;
            const scoreData = scores.get(key);

            if (scoreData?.score !== null && scoreData?.score !== undefined) {
                // Determine reliability (default 1.0 if not found, though types say it's there)
                // We access scores directly which has reliability
                const reliability = (scoreData as any).reliability ?? 1.0;
                // Type casting because the interface passed is simplified?
                // Actually Map value is { score: number | null }, reliability missing in Prop interface
                // But caller passes full object.
                // It's safer if I update the interface.

                const effectiveScore = scoreData.score * reliability;
                weightedSum += effectiveScore * weightSub.weighting;
                totalWeight += weightSub.weighting;
                hasAnyScore = true;
            }
        }

        if (!hasAnyScore || totalWeight === 0) return null;
        return Math.round((weightedSum / totalWeight) * 10) / 10;
    }, [weightsData, scores, enNameMap, activeWeightProfile]);

    // Pre-calculate all category scores (memoized for proper reactivity)
    const categoryScores = useMemo(() => {
        const result: Record<string, number | null> = {};
        for (const cat of categoryData) {
            result[cat.category] = calculateCategoryScore(cat.category);
        }
        return result;
    }, [categoryData, calculateCategoryScore]);

    // Calculate weighted cluster-level score (using category weights)
    const clusterScore = useMemo(() => {
        if (!weightsData) return null;

        // Dynamically resolving cluster weight
        if (activeWeightProfile && activeWeightProfile.weight_tree) {
            const clusterNode = activeWeightProfile.weight_tree.find((n: any) =>
                n.key === weightsData.cluster || n.name_en?.toUpperCase() === weightsData.cluster
            );
            if (clusterNode && clusterNode.weighting === 0) return null;
        } else {
            if (weightsData.weighting === 0) return null;
        }

        let totalWeight = 0;
        let weightedSum = 0;
        let hasAnyScore = false;

        // Build a map from EN category name to locale category name
        const enToLocaleCategoryMap = new Map<string, string>();
        for (const [localeKey, enNames] of enNameMap.entries()) {
            // localeKey format: "LocaleCategory::LocaleSubcategory"
            const localeCat = localeKey.split('::')[0];
            if (!enToLocaleCategoryMap.has(enNames.enCategory)) {
                enToLocaleCategoryMap.set(enNames.enCategory, localeCat);
            }
        }

        // We iterate over weightsData.categories to know which categories exist in this cluster
        // But we need to use dynamic weights if available
        for (const weightCat of weightsData.categories) {

            let weighting = weightCat.weighting;

            // Resolve dynamic weight
            if (activeWeightProfile && activeWeightProfile.weight_tree) {
                const clusterNode = activeWeightProfile.weight_tree.find((n: any) =>
                    n.key === weightsData.cluster || n.name_en?.toUpperCase() === weightsData.cluster
                );

                if (clusterNode) {
                    const categoryNode = clusterNode.children?.find((c: any) => c.name_en === weightCat.name);
                    if (categoryNode) {
                        weighting = categoryNode.weighting;
                    }
                }
            }


            // Get locale category name using the mapping
            const localeCatName = enToLocaleCategoryMap.get(weightCat.name) || weightCat.name;
            const catScore = categoryScores[localeCatName];

            if (catScore !== null) {
                weightedSum += catScore * weighting;
                totalWeight += weighting;
                hasAnyScore = true;
            }
        }

        if (!hasAnyScore || totalWeight === 0) return null;
        return Math.round((weightedSum / totalWeight) * 10) / 10;
    }, [weightsData, categoryScores, enNameMap, activeWeightProfile]);

    // Calculate overall score across ALL clusters
    const overallScore = useMemo(() => {
        if (allWeightsData.length === 0 || Object.keys(allCategoryData).length === 0) return null;

        const clusterResults: { cluster: string; score: number | null }[] = [];

        for (const clusterName of ['ENVIRONMENT', 'DESIGN', 'ECONOMIC', 'SOCIAL']) {
            const clusterWeights = allWeightsData.find(w => w.cluster === clusterName);
            const clusterCats = allCategoryData[clusterName] || [];

            if (!clusterWeights || clusterCats.length === 0) {
                clusterResults.push({ cluster: clusterName, score: null });
                continue;
            }

            // Resolve dynamic cluster node
            let clusterWeighting = clusterWeights.weighting;
            let dynamicClusterNode: any = null;

            if (activeWeightProfile && activeWeightProfile.weight_tree) {
                dynamicClusterNode = activeWeightProfile.weight_tree.find((n: any) =>
                    n.key === clusterName || n.name_en?.toUpperCase() === clusterName
                );
                if (dynamicClusterNode) {
                    clusterWeighting = dynamicClusterNode.weighting;
                }
            }

            if (clusterWeighting === 0) {
                clusterResults.push({ cluster: clusterName, score: null });
                continue;
            }

            let totalWeight = 0;
            let weightedSum = 0;
            let hasAnyScore = false;

            for (const weightCat of clusterWeights.categories) {

                let catWeighting = weightCat.weighting;
                let dynamicCatNode: any = null;

                if (dynamicClusterNode) {
                    dynamicCatNode = dynamicClusterNode.children?.find((c: any) => c.name_en === weightCat.name);
                    if (dynamicCatNode) {
                        catWeighting = dynamicCatNode.weighting;
                    }
                }

                if (catWeighting === 0) continue;

                // Calculate category score
                let catTotalWeight = 0;
                let catWeightedSum = 0;
                let catHasAnyScore = false;

                for (const weightSub of weightCat.subcategories) {
                    // Get dynamic weight
                    let subWeighting = weightSub.weighting;
                    if (dynamicCatNode) {
                        const dynamicSubNode = dynamicCatNode.children?.find((s: any) => s.name_en === weightSub.name);
                        if (dynamicSubNode) {
                            subWeighting = dynamicSubNode.weighting;
                        }
                    }

                    if (subWeighting === 0) continue;

                    // Construct key in format: CLUSTER::category_en::subcategory_en
                    // weightCat.name and weightSub.name are already in English from weights file
                    const key = `${clusterName}::${weightCat.name}::${weightSub.name}`;
                    const scoreData = allScores.get(key);

                    if (scoreData?.score !== null && scoreData?.score !== undefined) {
                        const reliability = scoreData.reliability ?? 1.0;
                        const effectiveScore = scoreData.score * reliability;
                        catWeightedSum += effectiveScore * subWeighting;
                        catTotalWeight += subWeighting;
                        catHasAnyScore = true;
                    }
                }

                if (catHasAnyScore && catTotalWeight > 0) {
                    const catScore = catWeightedSum / catTotalWeight;
                    weightedSum += catScore * catWeighting;
                    totalWeight += catWeighting;
                    hasAnyScore = true;
                }
            }

            if (hasAnyScore && totalWeight > 0) {
                clusterResults.push({ cluster: clusterName, score: Math.round((weightedSum / totalWeight) * 10) / 10 });
            } else {
                clusterResults.push({ cluster: clusterName, score: null });
            }
        }

        const scoredClusters = clusterResults.filter(c => c.score !== null);
        if (scoredClusters.length === 0) return null;

        const sum = scoredClusters.reduce((acc, c) => acc + (c.score || 0), 0);
        return Math.round((sum / scoredClusters.length) * 10) / 10;
    }, [allWeightsData, allCategoryData, allScores, activeWeightProfile]);

    return {
        getScoreColor,
        progress,
        categoryScores,
        clusterScore,
        overallScore
    };
}
